import type { ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import {
  Editor,
  formTemplateDetailQuery,
  usePublishTemplate,
  useUpdateTemplate,
  type FormField,
} from '~/features/form-builder'
import { logger } from '~/lib/logger'
import { isAppError } from '~/lib/result'

/**
 * `/admin/form-builder/$templateId` — three-pane editor.
 *
 * Layout per `14-form-builder.md` §D — see `Editor` for the canonical
 * 3-column structure that uses the `.ka-fb-column` class on each pane
 * (HANDOFF §5 invariant 6).
 *
 * The matching admin-api endpoints don't exist yet; if the GET returns
 * `not_found` we render a calm empty state pointing the user back to
 * the templates list. The save/publish mutations write through the
 * normal hooks — they'll start working as soon as the Worker side
 * lands.
 */
export const Route = createFileRoute('/admin/form-builder/$templateId')({
  component: FormBuilderEditorPage,
})

function FormBuilderEditorPage(): ReactElement {
  const navigate = useNavigate()
  const { templateId } = Route.useParams()
  const templateQuery = useQuery(formTemplateDetailQuery(templateId))

  const updateMutation = useUpdateTemplate()
  const publishMutation = usePublishTemplate()

  const goBack = (): void => {
    void navigate({ to: '/admin/form-builder' })
  }

  if (templateQuery.isLoading) {
    return <EditorSkeleton onBack={goBack} />
  }

  if (templateQuery.error) {
    const isNotFound =
      isAppError(templateQuery.error) &&
      templateQuery.error.code === 'not_found'
    return (
      <div className="ka-page">
        <div className="ka-card" role="alert">
          <div className="ka-empty">
            <div className="ka-empty-title">
              {isNotFound ? 'Template not available yet' : 'Couldn’t load template'}
            </div>
            <div className="ka-empty-body">
              {isNotFound
                ? 'The form-builder API hasn’t been wired up yet. Once the matching admin-api routes land, this editor will populate from the server.'
                : 'Something went wrong while reading this template. Refresh to try again — if the problem persists, the API may be down.'}
            </div>
            <div className="ka-empty-actions">
              <button
                type="button"
                className="ka-btn ka-btn--ghost"
                onClick={goBack}
              >
                Back to templates
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const template = templateQuery.data
  if (!template) {
    return <EditorSkeleton onBack={goBack} />
  }

  const handleSave = (fields: ReadonlyArray<FormField>): void => {
    updateMutation.mutate(
      { templateId, patch: { fields } },
      {
        onError: (cause) =>
          logger.warn('form-builder save failed', { cause, templateId }),
      },
    )
  }

  const handlePublish = (input: {
    notes: string
    force_replace: boolean
  }): void => {
    publishMutation.mutate(
      { templateId, ...input },
      {
        onError: (cause) =>
          logger.warn('form-builder publish failed', { cause, templateId }),
      },
    )
  }

  return (
    <div className="ka-page">
      <Editor
        template={template}
        isPublishing={publishMutation.isPending}
        onBack={goBack}
        onSave={handleSave}
        onPublish={handlePublish}
      />
    </div>
  )
}

function EditorSkeleton({ onBack }: { readonly onBack: () => void }): ReactElement {
  return (
    <div className="ka-page" aria-busy="true" aria-label="Loading template">
      <header className="ka-detail-page-header">
        <div className="ka-detail-page-row">
          <button
            type="button"
            className="ka-btn ka-btn--ghost ka-btn--sm"
            onClick={onBack}
          >
            ← Templates
          </button>
          <div className="ka-sk ka-sk--icon ka-sk--ml" />
        </div>
      </header>
      <div className="ka-fb-grid">
        <div className="ka-card ka-fb-column ka-card-body">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="ka-sk ka-sk--field" />
          ))}
        </div>
        <div className="ka-card ka-fb-column ka-card-body--lg">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="ka-sk ka-sk--input" />
          ))}
        </div>
        <div className="ka-card ka-fb-column ka-card-body">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ka-sk ka-sk--field" />
          ))}
        </div>
      </div>
    </div>
  )
}
